import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  TablePagination,
  SelectChangeEvent,
  CircularProgress,
  Grid,
  TextField,
  InputAdornment,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AssignmentReturnIcon from '@mui/icons-material/AssignmentReturn';
import PrintIcon from '@mui/icons-material/Print';
import SearchIcon from '@mui/icons-material/Search';
import { useSnackbar } from 'notistack';
import { format, parseISO } from 'date-fns';
import { ru, enUS } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../contexts/LanguageContext';
import api from '../api';

interface Assignment {
  id: number;
  deviceId: number;
  device: { id: number; inventoryNumber: string; model: string; type: string };
  employeeId: number;
  employee: { id: number; fullName: string; position: string | null; organization: { id: number; name: string } };
  issueDate: string;
  expectedReturnDate: string | null;
  returnDate: string | null;
}

interface Employee {
  id: number;
  fullName: string;
  organization: { id: number; name: string };
}

interface AvailableDevice {
  id: number;
  inventoryNumber: string;
  model: string;
  type: string;
}

interface ActData {
  id: number;
  issueDate: string;
  device: {
    id: number;
    inventoryNumber: string;
    type: string;
    model: string;
    serialNumber: string | null;
    cost: number | null;
  };
  employee: {
    id: number;
    fullName: string;
    position: string | null;
    organization: { id: number; name: string };
  };
}

export default function AssignmentsPage() {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const locale = useMemo(() => language === 'ru' ? ru : enUS, [language]);
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const rowsPerPage = 20;

  const [issueOpen, setIssueOpen] = useState(false);
  const [availableDevices, setAvailableDevices] = useState<AvailableDevice[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [issueForm, setIssueForm] = useState({
    deviceId: '' as number | '',
    employeeId: '' as number | '',
    issueDate: new Date().toISOString().split('T')[0],
    expectedReturnDate: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const [returnOpen, setReturnOpen] = useState(false);
  const [returnAssignment, setReturnAssignment] = useState<Assignment | null>(null);
  const [returnDate, setReturnDate] = useState(new Date().toISOString().split('T')[0]);

  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const fetchAssignments = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page', String(page + 1));
      params.set('limit', String(rowsPerPage));
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (search) params.set('search', search);

      const { data } = await api.get(`/assignments?${params}`);
      setAssignments(data.data);
      setTotal(data.total);
    } catch (err: any) {
      enqueueSnackbar(err.response?.data?.error || t('assignments.errorLoading'), { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, search, enqueueSnackbar, t]);

  useEffect(() => {
    fetchAssignments();
  }, [fetchAssignments]);

  const openIssueDialog = async () => {
    try {
      const [devRes, empRes] = await Promise.all([
        api.get('/devices?status=in_stock&limit=200'),
        api.get('/employees'),
      ]);
      setAvailableDevices(devRes.data.data);
      setEmployees(empRes.data);
      setIssueForm({
        deviceId: '',
        employeeId: '',
        issueDate: new Date().toISOString().split('T')[0],
        expectedReturnDate: '',
      });
      setIssueOpen(true);
    } catch (err: any) {
      enqueueSnackbar(t('assignments.errorLoadingData'), { variant: 'error' });
    }
  };

  const handleIssue = async () => {
    if (!issueForm.deviceId || !issueForm.employeeId) return;
    setSubmitting(true);
    try {
      await api.post('/assignments', {
        deviceId: issueForm.deviceId,
        employeeId: issueForm.employeeId,
        issueDate: issueForm.issueDate || undefined,
        expectedReturnDate: issueForm.expectedReturnDate || null,
      });
      enqueueSnackbar(t('assignments.snackbarIssued'), { variant: 'success' });
      setIssueOpen(false);
      fetchAssignments();
    } catch (err: any) {
      enqueueSnackbar(err.response?.data?.error || t('common.error'), { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleReturn = async () => {
    if (!returnAssignment) return;
    try {
      await api.put(`/assignments/${returnAssignment.id}/return`, {
        returnDate: returnDate || undefined,
      });
      enqueueSnackbar(t('assignments.snackbarReturned'), { variant: 'success' });
      setReturnOpen(false);
      setReturnAssignment(null);
      fetchAssignments();
    } catch (err: any) {
      enqueueSnackbar(err.response?.data?.error || t('common.error'), { variant: 'error' });
    }
  };

  const handlePrintAct = async (assignmentId: number) => {
    try {
      const { data: act } = await api.get<ActData>(`/assignments/${assignmentId}/act-data`);
      const deviceName = `${act.device.model} / ${act.device.type} (инв. № ${act.device.inventoryNumber}${act.device.serialNumber ? ', с/н ' + act.device.serialNumber : ''})`;
      const costDisplay = act.device.cost != null
        ? `${act.device.cost.toLocaleString('ru-RU')} ₽`
        : '______________';
      const costTotal = act.device.cost != null
        ? `${act.device.cost.toLocaleString('ru-RU')} руб.`
        : '______________ руб.';
      const issueDateFormatted = format(parseISO(act.issueDate), 'dd.MM.yyyy', { locale });
      const orgName = act.employee.organization.name;
      const employeeName = act.employee.fullName;
      const employeePosition = act.employee.position || '______________';

      const printWindow = window.open('', '_blank', 'width=900,height=700');
      if (!printWindow) {
        enqueueSnackbar(t('common.error'), { variant: 'error' });
        return;
      }

      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="utf-8" />
          <title>${t('assignments.actTitle')}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: 'Times New Roman', serif; font-size: 13pt; line-height: 1.6; color: #000; padding: 40px 50px; }
            h2 { text-align: center; font-size: 14pt; margin-bottom: 10px; font-weight: bold; }
            .date-line { text-align: right; margin-bottom: 16px; font-size: 13pt; }
            p { margin-bottom: 12px; text-align: justify; }
            table { width: 100%; border-collapse: collapse; margin: 16px 0; }
            th, td { border: 1px solid #000; padding: 6px 8px; text-align: center; font-size: 12pt; }
            th { font-weight: bold; background: #f0f0f0; }
            td:nth-child(2) { text-align: left; }
            .total { margin: 8px 0 16px; font-weight: bold; }
            .sig-table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            .sig-table td { border: 1px solid #000; padding: 12px 10px; vertical-align: top; text-align: left; width: 50%; font-size: 12pt; }
            .sig-table td:first-child { border-right: 1px solid #000; }
            .sig-label { font-weight: bold; margin-bottom: 20px; display: block; }
            @media print {
              body { padding: 15px 20px; }
              @page { size: A4; margin: 20mm; }
            }
          </style>
        </head>
        <body>
          <h2>${t('assignments.actTitle')}</h2>
          <div class="date-line">${issueDateFormatted}</div>
          <p><strong>${orgName}</strong> ${t('assignments.actPreamble')} <strong>${employeeName}</strong> ${t('assignments.actPreambleEmployee')}</p>
          <p>${t('assignments.actContractRef')}</p>
          <table>
            <thead>
              <tr>
                <th>№ п/п</th>
                <th>${t('assignments.actTableItem')}</th>
                <th>${t('assignments.actTableQuantity')}</th>
                <th>${t('assignments.actTableCost')}</th>
                <th>${t('assignments.actTableSignature')}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>1</td>
                <td>${deviceName}</td>
                <td>1</td>
                <td>${costDisplay}</td>
                <td></td>
              </tr>
            </tbody>
          </table>
          <p class="total">${t('assignments.actTotal')} ${costTotal}</p>
          <p>${t('assignments.actChecked')}</p>
          <p>${t('assignments.actCopies')}</p>
          <table class="sig-table">
            <tr>
              <td>
                <span class="sig-label">${t('assignments.actEmployer')}</span>
                ${t('assignments.actGeneralDirector')}<br/><br/>
                ${orgName}<br/><br/>
                ________________ / Каджая Г.Н.
              </td>
              <td>
                <span class="sig-label">${t('assignments.actEmployee')}</span>
                ${employeePosition}<br/><br/>
                ________________ / ${employeeName}
              </td>
            </tr>
          </table>
          <script>
            window.onload = function() { window.print(); };
          </script>
        </body>
        </html>
      `);
      printWindow.document.close();
    } catch (err: any) {
      enqueueSnackbar(err.response?.data?.error || t('common.error'), { variant: 'error' });
    }
  };

  return (
    <Box>
      {/* Intro */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: 2,
          mb: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, color: 'var(--text-muted)' }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1.5,
              background: isDark
                ? 'linear-gradient(135deg, #8b8bff 20%, #5ee7f1 80%)'
                : 'linear-gradient(135deg, #5b5bf6 20%, #22c3d1 80%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              boxShadow: isDark
                ? '0 8px 20px -8px rgba(139,139,255,0.6)'
                : '0 8px 20px -8px rgba(91,91,246,0.4)',
            }}
          >
            <AssignmentReturnIcon sx={{ fontSize: 20 }} />
          </Box>
          <Typography variant="body2">
            {t('assignments.intro')} · {t('assignments.total')}: <strong style={{ color: 'var(--text)' }}>{total}</strong>
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openIssueDialog}>
          {t('assignments.issueDevice')}
        </Button>
      </Box>

      {/* Filter bar */}
      <Paper
        sx={{
          p: 2,
          mb: 3,
          borderRadius: 1.5,
          border: '1px solid var(--border)',
          background: 'var(--surface)',
        }}
      >
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            sx={{ flex: '1 1 300px' }}
            placeholder={t('assignments.searchPlaceholder')}
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: 'var(--text-muted)' }} />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>{t('common.status')}</InputLabel>
            <Select
              value={statusFilter}
              label={t('common.status')}
              onChange={(e: SelectChangeEvent) => { setStatusFilter(e.target.value); setPage(0); }}
            >
              <MenuItem value="all">{t('common.all')}</MenuItem>
              <MenuItem value="active">{t('assignments.filterActive')}</MenuItem>
              <MenuItem value="returned">{t('assignments.filterReturned')}</MenuItem>
            </Select>
          </FormControl>
          {(search || statusFilter !== 'all') && (
            <Button
              size="small"
              onClick={() => { setSearch(''); setStatusFilter('all'); setPage(0); }}
            >
              {t('devices.clearFilters')}
            </Button>
          )}
        </Box>
      </Paper>

      <Paper sx={{ borderRadius: 1.5, overflow: 'hidden' }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('assignments.tableDevice')}</TableCell>
                <TableCell>{t('assignments.tableEmployee')}</TableCell>
                <TableCell>{t('assignments.tableIssueDate')}</TableCell>
                <TableCell>{t('assignments.tableExpectedReturn')}</TableCell>
                <TableCell>{t('assignments.tableStatus')}</TableCell>
                <TableCell align="right">{t('common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center"><CircularProgress size={24} sx={{ my: 2 }} /></TableCell>
                </TableRow>
              ) : assignments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'var(--text-muted)' }}>{t('assignments.noRecords')}</TableCell>
                </TableRow>
              ) : (
                assignments.map((a) => (
                  <TableRow key={a.id}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 600 }}>
                        {a.device.inventoryNumber}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'var(--text-muted)', display: 'block' }}>
                        {a.device.model} · {a.device.type}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {a.employee.fullName}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'var(--text-muted)' }}>
                        {a.employee.organization.name}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'JetBrains Mono, monospace', color: 'var(--text-muted)' }}>
                      {format(parseISO(a.issueDate), 'dd.MM.yyyy', { locale })}
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'JetBrains Mono, monospace', color: 'var(--text-muted)' }}>
                      {a.expectedReturnDate
                        ? format(parseISO(a.expectedReturnDate), 'dd.MM.yyyy', { locale })
                        : '—'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={a.returnDate ? t('assignments.statusReturned') : t('assignments.statusActive')}
                        color={a.returnDate ? 'success' : 'primary'}
                        size="small"
                        sx={{
                          background: a.returnDate
                            ? isDark ? 'rgba(52,211,153,0.15)' : 'rgba(16,185,129,0.12)'
                            : isDark ? 'rgba(139,139,255,0.15)' : 'rgba(91,91,246,0.12)',
                          color: 'inherit',
                          border: 'none',
                          fontWeight: 600,
                        }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', gap: 0.75, justifyContent: 'flex-end' }}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handlePrintAct(a.id)}
                        >
                          <PrintIcon fontSize="small" sx={{ mr: 0.5 }} />
                          {t('assignments.printActButton')}
                        </Button>
                        {!a.returnDate && (
                        <Button
                          size="small"
                          variant="outlined"
                          color="success"
                          onClick={() => {
                            setReturnAssignment(a);
                            setReturnDate(new Date().toISOString().split('T')[0]);
                            setReturnOpen(true);
                          }}
                        >
                          {t('assignments.returnButton')}
                        </Button>
                      )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={() => {}}
          labelRowsPerPage={t('common.rowsPerPage')}
          labelDisplayedRows={({ from, to, count }) => t('common.fromToOf', { from, to, count })}
        />
      </Paper>

      {/* Issue dialog */}
      <Dialog open={issueOpen} onClose={() => setIssueOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('assignments.issueDevice')}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>{t('assignments.formDevice')}</InputLabel>
                <Select
                  value={issueForm.deviceId}
                  label={t('assignments.formDevice')}
                  onChange={(e) => setIssueForm({ ...issueForm, deviceId: e.target.value as number })}
                >
                  {availableDevices.map((d) => (
                    <MenuItem key={d.id} value={d.id}>
                      {d.inventoryNumber} — {d.model} ({d.type})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>{t('assignments.formEmployee')}</InputLabel>
                <Select
                  value={issueForm.employeeId}
                  label={t('assignments.formEmployee')}
                  onChange={(e) => setIssueForm({ ...issueForm, employeeId: e.target.value as number })}
                >
                  {employees.map((emp) => (
                    <MenuItem key={emp.id} value={emp.id}>
                      {emp.fullName} ({emp.organization.name})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth label={t('assignments.formIssueDate')} type="date"
                InputLabelProps={{ shrink: true }}
                value={issueForm.issueDate}
                onChange={(e) => setIssueForm({ ...issueForm, issueDate: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth label={t('assignments.formExpectedReturnDate')} type="date"
                InputLabelProps={{ shrink: true }}
                value={issueForm.expectedReturnDate}
                onChange={(e) => setIssueForm({ ...issueForm, expectedReturnDate: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIssueOpen(false)}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            onClick={handleIssue}
            disabled={submitting || !issueForm.deviceId || !issueForm.employeeId}
          >
            {submitting ? <CircularProgress size={20} /> : t('assignments.issueButton')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Return dialog */}
      <Dialog open={returnOpen} onClose={() => setReturnOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('assignments.returnDevice')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1, color: 'var(--text-muted)' }}>
            {returnAssignment?.device.inventoryNumber} — {returnAssignment?.device.model}
          </Typography>
          <Typography variant="body2" sx={{ mb: 2.5, color: 'var(--text-muted)' }}>
            {t('common.employee')}: <span style={{ color: 'var(--text)', fontWeight: 500 }}>{returnAssignment?.employee.fullName}</span>
          </Typography>
          <TextField
            fullWidth
            label={t('assignments.formReturnDate')}
            type="date"
            InputLabelProps={{ shrink: true }}
            value={returnDate}
            onChange={(e) => setReturnDate(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReturnOpen(false)}>{t('common.cancel')}</Button>
          <Button variant="contained" color="success" onClick={handleReturn} autoFocus>
            {t('assignments.confirmReturn')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}